/**
 * Cost centres, godowns and batches.
 *
 * All three were extracted from day one and never shown. They are derived here
 * rather than in the agent because everything the app needs is already in the
 * snapshot — adding a report must never mean another round trip to Tally.
 *
 * ONE HONEST LIMITATION runs through the inventory half. The snapshot carries a
 * single opening/closing pair per stock ITEM, not per godown and not per batch,
 * because that is what Tally's StockItem collection returns. So godown- and
 * batch-level figures here are built from voucher MOVEMENT only. For a company
 * whose books start inside the snapshot window the two agree; for one with
 * opening stock lying in a godown before the window, they do not. The screens
 * say so rather than presenting a number that quietly excludes opening stock.
 */
import type { Snapshot, Voucher } from "@tally/shared";

import { postedVouchers } from "./reports.js";

/* ────────────────────────────────────────────────────────── cost centres ── */

export type CostCentreRow = {
  costCentre: string;
  category: string;
  /** signed, debit-negative — the sum of allocations touching this centre */
  net: number;
  debit: number;
  credit: number;
  entries: number;
};

export type CostCentreReport = {
  rows: CostCentreRow[];
  /** allocations found on ledger entries whose voucher is not cancelled */
  totalAllocated: number;
  /** true when the books simply do not use cost centres */
  unused: boolean;
};

export function deriveCostCentres(snapshot: Snapshot): CostCentreReport {
  const byCentre = new Map<string, CostCentreRow>();

  for (const voucher of postedVouchers(snapshot)) {
    for (const entry of voucher.ledgerEntries) {
      for (const allocation of entry.costAllocations) {
        const key = `${allocation.costCentre} ${allocation.category ?? ""}`;
        const row =
          byCentre.get(key) ??
          {
            costCentre: allocation.costCentre,
            category: allocation.category ?? "—",
            net: 0,
            debit: 0,
            credit: 0,
            entries: 0,
          };

        row.net += allocation.amount;
        if (allocation.amount < 0) row.debit += -allocation.amount;
        else row.credit += allocation.amount;
        row.entries += 1;

        byCentre.set(key, row);
      }
    }
  }

  const rows = [...byCentre.values()].sort((a, b) => Math.abs(b.net) - Math.abs(a.net));
  return {
    rows,
    totalAllocated: rows.reduce((sum, r) => sum + r.net, 0),
    unused: rows.length === 0,
  };
}

/* ─────────────────────────────────────────────────────────────── godowns ── */

export type GodownItemRow = { stockItem: string; unit: string; inQty: number; outQty: number; net: number };

export type GodownRow = {
  godown: string;
  items: GodownItemRow[];
  /** net value moved through this godown, for ordering only */
  netValue: number;
};

export type GodownReport = { rows: GodownRow[]; unused: boolean };

/**
 * Movement per godown.
 *
 * Sign convention: Tally's inventory qty is positive on inward and negative on
 * outward, matching the amount sign on the same entry. `net` is therefore what
 * the godown gained over the window — NOT its closing stock (see the note at
 * the top of this file).
 */
export function deriveGodowns(snapshot: Snapshot): GodownReport {
  const byGodown = new Map<string, Map<string, GodownItemRow>>();
  const value = new Map<string, number>();

  for (const voucher of postedVouchers(snapshot)) {
    for (const entry of voucher.inventoryEntries) {
      const godown = entry.godown?.trim();
      if (!godown) continue;

      const items = byGodown.get(godown) ?? new Map<string, GodownItemRow>();
      const row =
        items.get(entry.stockItem) ??
        { stockItem: entry.stockItem, unit: entry.unit, inQty: 0, outQty: 0, net: 0 };

      if (entry.qty >= 0) row.inQty += entry.qty;
      else row.outQty += -entry.qty;
      row.net += entry.qty;

      items.set(entry.stockItem, row);
      byGodown.set(godown, items);
      value.set(godown, (value.get(godown) ?? 0) + entry.amount);
    }
  }

  const rows: GodownRow[] = [...byGodown.entries()]
    .map(([godown, items]) => ({
      godown,
      items: [...items.values()].sort((a, b) => Math.abs(b.net) - Math.abs(a.net)),
      netValue: value.get(godown) ?? 0,
    }))
    .sort((a, b) => Math.abs(b.netValue) - Math.abs(a.netValue));

  return { rows, unused: rows.length === 0 };
}

/* ─────────────────────────────────────────────────────────────── batches ── */

export type BatchRow = {
  stockItem: string;
  batch: string;
  unit: string;
  /** net quantity movement for this batch */
  qty: number;
  expiryDate?: string;
  /** negative once expired; undefined when the batch carries no expiry */
  daysToExpiry?: number;
  status: "expired" | "expiring" | "ok" | "no-expiry";
};

export type BatchReport = {
  rows: BatchRow[];
  expired: BatchRow[];
  expiring: BatchRow[];
  unused: boolean;
};

/** Anything inside this window is worth a merchant's attention today. */
export const EXPIRING_SOON_DAYS = 90;

export function deriveBatches(snapshot: Snapshot, asOf: string): BatchReport {
  const byBatch = new Map<string, BatchRow>();

  for (const voucher of postedVouchers(snapshot)) {
    for (const entry of voucher.inventoryEntries) {
      const batch = entry.batch?.trim();
      if (!batch) continue;

      const key = `${entry.stockItem} ${batch}`;
      const row =
        byBatch.get(key) ??
        {
          stockItem: entry.stockItem,
          batch,
          unit: entry.unit,
          qty: 0,
          ...(entry.expiryDate ? { expiryDate: entry.expiryDate } : {}),
          status: "no-expiry" as const,
        };

      row.qty += entry.qty;
      // A later voucher can be the one carrying the expiry date; keep the first
      // non-empty one rather than letting a blank overwrite it.
      if (!row.expiryDate && entry.expiryDate) row.expiryDate = entry.expiryDate;

      byBatch.set(key, row);
    }
  }

  const rows = [...byBatch.values()].map((row) => {
    if (!row.expiryDate) return { ...row, status: "no-expiry" as const };

    const days = daysBetween(asOf, row.expiryDate);
    const status: BatchRow["status"] =
      days < 0 ? "expired" : days <= EXPIRING_SOON_DAYS ? "expiring" : "ok";
    return { ...row, daysToExpiry: days, status };
  });

  // Soonest expiry first; batches without one sink to the bottom where they
  // belong — they are never the thing you opened this screen for.
  rows.sort((a, b) => {
    if (a.daysToExpiry === undefined && b.daysToExpiry === undefined) return 0;
    if (a.daysToExpiry === undefined) return 1;
    if (b.daysToExpiry === undefined) return -1;
    return a.daysToExpiry - b.daysToExpiry;
  });

  // Stock already sold out is not a problem waiting to happen. Only batches
  // still holding quantity are worth flagging.
  const holding = (row: BatchRow) => row.qty > 0.0001;

  return {
    rows,
    expired: rows.filter((r) => r.status === "expired" && holding(r)),
    expiring: rows.filter((r) => r.status === "expiring" && holding(r)),
    unused: rows.length === 0,
  };
}

function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}

/** Exposed for tests: which vouchers count. */
export function countedVouchers(snapshot: Snapshot): Voucher[] {
  return postedVouchers(snapshot);
}
